import { toCents } from './to-cents.function';

/**
 * Spreads a stored money total across weights, in cents.
 *
 * The last weight receives whatever cents are left, so the shares add back to
 * the total exactly. A zero or missing total allocates nothing. When every
 * weight is zero or negative, the whole total goes on the last share.
 *
 * @param total The stored money total. `null` and `undefined` allocate nothing.
 * @param weights One weight per share, in the order the shares are returned.
 * @returns One cent amount per weight, in the same order.
 * @example
 * ```ts
 * allocateCents(10, [100, 300]); // [250, 750]
 * ```
 * @example
 * ```ts
 * allocateCents(0.01, [1, 1, 1]); // [0, 0, 1]
 * ```
 */
export function allocateCents(
  total: number | null | undefined,
  weights: number[],
): number[] {
  const cents = toCents(total);

  if (weights.length === 0 || cents === 0) {
    return weights.map(() => 0);
  }

  const weightSum = weights.reduce((sum, weight) => sum + weight, 0);

  if (weightSum <= 0) {
    return weights.map((_, index) =>
      index === weights.length - 1 ? cents : 0,
    );
  }

  let assigned = 0;

  return weights.map((weight, index) => {
    if (index === weights.length - 1) {
      return cents - assigned;
    }

    const share = Math.floor((cents * weight) / weightSum);
    assigned += share;
    return share;
  });
}
